/**
 * Routes that must work WITHOUT sign-in (the customer's secure report link
 * /r/:token, the invitation's set-password page /welcome/:token, the terms
 * and privacy texts /legal/:key) are rendered here, outside the AuthGate. Everything else goes
 * through the gate as usual. The page itself is a lazy chunk.
 */
import type { ReactNode } from 'react';
import { lazyPage } from '../../app/lazy';
import { matchPath, useLocation, useRoutes } from '../../lib/router';

const PUBLIC_PATHS = ['/r/:token', '/remediate/:token', '/portfolio/:token', '/welcome/:token', '/legal/:key', '/native-auth'];

const PUBLIC_ROUTES = [
  { path: '/r/:token', element: lazyPage(() => import('./PublicReportPage'), 'PublicReportPage') },
  { path: '/remediate/:token', element: lazyPage(() => import('./ContractorRemediatePage'), 'ContractorRemediatePage') },
  { path: '/portfolio/:token', element: lazyPage(() => import('./CustomerPortfolioPage'), 'CustomerPortfolioPage') },
  // A local-account user choosing their password from the invitation email.
  { path: '/welcome/:token', element: lazyPage(() => import('../welcome/WelcomePage'), 'WelcomePage') },
  // Terms of use / privacy notice linked from the sign-in page (admin legal texts).
  { path: '/legal/:key', element: lazyPage(() => import('./LegalTextPage'), 'LegalTextPage') },
  // System-browser Google OAuth relay for the native Android & iOS mobile apps.
  { path: '/native-auth', element: lazyPage(() => import('./NativeGoogleAuthPage'), 'NativeGoogleAuthPage') },
];

function PublicRoutes() {
  return useRoutes(PUBLIC_ROUTES);
}

export function PublicRouteSwitch({ children }: { children: ReactNode }) {
  const { pathname, search } = useLocation();
  const isNativeG4sIdCallback =
    pathname === '/' && new URLSearchParams(search).get('state')?.startsWith('g4sid_native_');
  if (isNativeG4sIdCallback) {
    const NativeAuth = lazyPage(() => import('./NativeGoogleAuthPage'), 'NativeGoogleAuthPage');
    return <>{NativeAuth}</>;
  }
  return PUBLIC_PATHS.some((p) => matchPath(p, pathname)) ? <PublicRoutes /> : <>{children}</>;
}
